import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiMinus, FiAward } from 'react-icons/fi';
import './WeeklyProgress.css';

const WeeklyProgress = ({ userId, mealLogs = [], progressEntries = [] }) => {
  const [weeklyStats, setWeeklyStats] = useState({
    compliance: 0,
    weightChange: 0,
    streak: 0,
    trend: 'stable'
  });

  useEffect(() => {
    calculateWeeklyStats();
  }, [mealLogs, progressEntries]);

  const calculateWeeklyStats = () => {
    // Calculate compliance (meals logged vs expected)
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);

    const thisWeekLogs = mealLogs.filter(log => {
      const logDate = log.timestamp?.toDate?.() || new Date(log.timestamp);
      return logDate >= weekAgo && logDate <= today;
    });

    const expectedMeals = 7 * 3; // 3 meals per day for 7 days
    const loggedMeals = thisWeekLogs.length;
    const compliance = Math.round((loggedMeals / expectedMeals) * 100);

    // Calculate weight change
    const sortedEntries = [...progressEntries]
      .filter(entry => entry.weight)
      .sort((a, b) => {
        const dateA = a.date?.toDate?.() || new Date(a.date);
        const dateB = b.date?.toDate?.() || new Date(b.date);
        return dateB - dateA;
      });

    const latestWeight = sortedEntries[0]?.weight || 0;
    const previousWeight = sortedEntries[7]?.weight || sortedEntries[sortedEntries.length - 1]?.weight || latestWeight;
    const weightChange = latestWeight - previousWeight;

    // Calculate logging streak
    const streak = calculateStreak(mealLogs);

    // Determine trend
    let trend = 'stable';
    if (weightChange < -0.2) trend = 'down';
    else if (weightChange > 0.2) trend = 'up';

    setWeeklyStats({
      compliance,
      weightChange,
      streak,
      trend
    });
  };

  const calculateStreak = (logs) => {
    const today = new Date();
    let currentDate = new Date(today);
    let streak = 0;

    while (true) {
      const dayLogs = logs.filter(log => {
        const logDate = log.timestamp?.toDate?.() || new Date(log.timestamp);
        return (
          logDate.getDate() === currentDate.getDate() &&
          logDate.getMonth() === currentDate.getMonth() &&
          logDate.getFullYear() === currentDate.getFullYear()
        );
      });

      if (dayLogs.length > 0) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }

      if (streak > 365) break; // Safety limit
    }

    return streak;
  };

  const getTrendIcon = () => {
    if (weeklyStats.trend === 'down') return <FiTrendingDown className="trend-icon down" />;
    if (weeklyStats.trend === 'up') return <FiTrendingUp className="trend-icon up" />;
    return <FiMinus className="trend-icon stable" />;
  };

  const getComplianceColor = () => {
    if (weeklyStats.compliance >= 80) return 'excellent';
    if (weeklyStats.compliance >= 60) return 'good';
    return 'needs-improvement';
  };

  return (
    <div className="weekly-progress-widget">
      <div className="widget-header">
        <h3>Weekly Progress</h3>
        <FiTrendingUp className="header-icon" />
      </div>

      <div className="progress-stats">
        {/* Compliance */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Compliance</span>
            <span className={`stat-badge ${getComplianceColor()}`}>
              {weeklyStats.compliance}%
            </span>
          </div>
          <div className="stat-bar">
            <div
              className={`stat-fill ${getComplianceColor()}`}
              style={{ width: `${weeklyStats.compliance}%` }}
            />
          </div>
          <span className="stat-hint">Average meal logging</span>
        </div>

        {/* Weight Change */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Weight Change</span>
            <div className="stat-value">
              {getTrendIcon()}
              <span className={`weight-change ${weeklyStats.trend}`}>
                {weeklyStats.weightChange > 0 ? '+' : ''}
                {weeklyStats.weightChange.toFixed(1)} kg
              </span>
            </div>
          </div>
          <span className="stat-hint">This week vs last week</span>
        </div>

        {/* Logging Streak */}
        <div className="stat-card highlight">
          <div className="stat-header">
            <span className="stat-label">Logging Streak</span>
            <div className="streak-badge">
              <FiAward />
              <span>{weeklyStats.streak} days</span>
            </div>
          </div>
          <span className="stat-hint">Keep it going!</span>
        </div>
      </div>

      <button className="view-progress-btn">
        View Full Progress
      </button>
    </div>
  );
};

export default WeeklyProgress;
